import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useProfileData = (profileId: string | undefined) => {
  const { toast } = useToast();

  const { data: currentUser } = useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  // Determine if we're viewing our own profile
  const isOwnProfile = !profileId || (currentUser && profileId === currentUser.id);

  const { data: subscription } = useQuery({
    queryKey: ["userSubscription", profileId],
    queryFn: async () => {
      if (!profileId && !currentUser) return null;
      const userId = profileId || currentUser?.id;

      const { data } = await supabase
        .from("user_subscriptions")
        .select("tier")
        .eq("user_id", userId)
        .maybeSingle();

      return data;
    },
    enabled: !!profileId || !!currentUser,
  });

  const { data: profile, isLoading, error, refetch } = useQuery({
    queryKey: ["profile", profileId],
    queryFn: async () => {
      if (!profileId && !currentUser) throw new Error("No profile ID available");
      const userId = profileId || currentUser?.id;

      console.log('Fetching profile data for user:', userId);
      
      const { data, error } = await supabase
        .from("profiles")
        .select(`
          *,
          artist_categories (name),
          experiences (*),
          education (*),
          certifications (*),
          portfolios (
            id,
            title,
            description,
            portfolio_items (*)
          )
        `)
        .eq("id", userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        toast({
          title: "Error",
          description: "Failed to load profile",
          variant: "destructive",
        });
        throw error;
      }

      console.log('Fetched profile data:', data);
      return data;
    },
    enabled: !!profileId || !!currentUser,
  });

  return {
    currentUser,
    isOwnProfile,
    subscription,
    profile,
    isLoading,
    error,
    refetch,
  };
};